import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { Typography } from '../../interfaces/typography';
import { MultiSelectOption } from '../Selectors/SimplifiedMultiSelect';

interface ClickableMetricProps {
  value: string | null;
  options: MultiSelectOption[];
  placeholder?: string;
  onSelect: (value: string | null) => void;
  disabled?: boolean;
  className?: string;
}

export const ClickableMetric: React.FC<ClickableMetricProps> = ({
  value,
  options,
  placeholder = "",
  onSelect,
  disabled = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchText("");
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const displayValue = value || placeholder;
  const isValueSet = value !== null && value !== undefined && value !== '';
  const shouldShowPlaceholder = !isValueSet && placeholder;

  // Color generation function (same as SessionList.tsx)
  const getTagColors = useCallback((tagValue: string) => {
    const stringToColor = (str: string) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      const h = hash % 360;
      return h;
    };

    const hue = stringToColor(tagValue);
    const bg = `hsl(${hue}, 70%, 90%)`;
    const text = `hsl(${hue}, 70%, 30%)`;

    return { bg, text };
  }, []);

  // Get colors for the current value
  const tagColors = isValueSet ? getTagColors(value!) : null;

  // Filter options based on search text
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleOptionSelect = (optionValue: string | null) => {
    onSelect(optionValue);
    setIsOpen(false);
    setSearchText("");
  };

  if (disabled || options.length === 0) {
    if (isValueSet && tagColors) {
      // Show as colored tag even when disabled
      return (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
          style={{
            backgroundColor: tagColors.bg,
            color: tagColors.text
          }}
        >
          {displayValue}
        </span>
      );
    }
    // Don't show anything if no value and no placeholder, or if disabled with no value
    if (!shouldShowPlaceholder) {
      return null;
    }
    return (
      <Typography variant="smallParagraph" color="gray">
        {displayValue}
      </Typography>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-opacity hover:opacity-80"
        style={
          isValueSet && tagColors
            ? {
                backgroundColor: tagColors.bg,
                color: tagColors.text,
                border: 'none'
              }
            : {
                backgroundColor: '#f9fafb',
                color: '#6b7280',
                border: '1px solid #d1d5db'
              }
        }
      >
        <span>{displayValue}</span>
        <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg" style={{ zIndex: 99999, minWidth: '120px', maxWidth: '200px', maxHeight: '200px', overflow: 'auto' }}>
          {/* Search input */}
          <div className="p-1 border-b border-gray-200">
            <input
              ref={inputRef}
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1 text-xs border-0 focus:outline-none"
              style={{ fontSize: '11px' }}
            />
          </div>

          {/* Clear option */}
          <button
            onClick={() => handleOptionSelect(null)}
            className="w-full px-2 py-1 text-left hover:bg-blue-50 flex items-center justify-between text-xs"
          >
            <Typography variant="smallParagraph" color="gray">
              Clear
            </Typography>
            {!isValueSet && <Check size={12} className="text-blue-500" />}
          </button>

          {/* Options */}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleOptionSelect(option.value)}
                className="w-full px-2 py-1 text-left hover:bg-blue-50 flex items-center justify-between text-xs"
              >
                <Typography variant="smallParagraph">
                  {option.label}
                </Typography>
                {value === option.value && <Check size={12} className="text-blue-500" />}
              </button>
            ))
          ) : (
            <div className="px-2 py-1">
              <Typography variant="smallParagraph" color="gray">
                No matches
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
};